import { useState, useMemo, useEffect } from "react";
import { Flex, Text, Button, Card, Badge, Separator, Progress, AlertDialog, Dialog, Select, TextField } from "@radix-ui/themes";
import { useBillingProducts, useCancelSubscription, useConfirmPaymentMethod, useDismissBillingFailureNotice, useRetryInvoicePayment, useUpdateBillingDetails } from "hooks/billing/useBilling";
import { useQueries } from "@tanstack/react-query";
import { getBillingDetails, getInvoices } from "api/BillingApi";
import { ActivateSubscriptionModal } from "./ActivateSubscriptionModal";
import { AddPaymentMethodModal } from "./AddPaymentMethodModal";
import { BackpackIcon, DotFilledIcon, Pencil1Icon } from "@radix-ui/react-icons";
import { CreditCardIcon, UserIcon, NewspaperIcon, ClipboardDocumentCheckIcon, BuildingLibraryIcon } from "@heroicons/react/24/outline";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import { ToastAlert } from "components";
import { useToast } from "../../context/ToastContext";

// Register the locale
countries.registerLocale(enLocale);

// Format currency helper
const formatCurrency = (amount, currency = "gbp") => {
  const currencySymbols = { gbp: "£", usd: "$", eur: "€" };
  const symbol = currencySymbols[currency.toLowerCase()] || currency.toUpperCase();
  const parsedAmount = parseFloat(amount ?? 0);
  return `${symbol}${Number.isNaN(parsedAmount) ? "0.00" : parsedAmount.toFixed(2)}`;
};

const isMeteredUsageProduct = (product) => product?.product_type === "metered_usage" || product?.is_metered_usage;

const getOrganisationProductPriceLabel = (orgProduct) => {
  if (isMeteredUsageProduct(orgProduct?.product)) {
    return "Usage-based";
  }

  return `${formatCurrency(parseFloat(orgProduct.product.price) * orgProduct.quantity, orgProduct.product.currency)}${
    orgProduct.product.billing_interval === "one_time" ? " (one-time)" : `/${orgProduct.product.billing_interval}`
  }`;
};

const getSubscriptionItemPriceLabel = (item) => {
  if (isMeteredUsageProduct(item)) {
    if (item.current_period_amount != null) {
      return `Est. ${formatCurrency(item.current_period_amount, item.currency)}`;
    }

    return "Usage-based";
  }

  if (item.amount == null) {
    return "Usage-based";
  }

  return `${formatCurrency(item.amount * item.quantity, item.currency)}/${item.interval}`;
};

const getSubscriptionItemPriceNote = (item) => {
  if (!isMeteredUsageProduct(item)) {
    return null;
  }

  if (item.current_period_amount != null) {
    return "Current period usage";
  }

  return "See Email Marketing usage settings";
};

const getFlatRecurringSubtotal = (items) => items.reduce((sum, item) => {
  if (isMeteredUsageProduct(item) || item.amount == null) {
    return sum;
  }
  return sum + parseFloat(item.amount) * item.quantity;
}, 0);

const getFlatOrganisationProductsTotal = (products) => products.reduce((sum, op) => {
  if (isMeteredUsageProduct(op.product)) {
    return sum;
  }
  return sum + parseFloat(op.product.price) * op.quantity;
}, 0);

const hasBillableAmount = (amount) => Number.parseFloat(amount ?? 0) >= 0.01;

const getInvoiceDisplayAmount = (invoice) => {
  if (invoice.status === "paid") {
    return invoice.amount_paid;
  }
  if (Number(invoice.amount_remaining) > 0) {
    return invoice.amount_remaining;
  }
  if (Number(invoice.amount_due) > 0) {
    return invoice.amount_due;
  }
  return invoice.total ?? 0;
};

const formatSortCode = (sortCode) => {
  if (!sortCode || sortCode.length !== 6) {
    return sortCode;
  }

  return `${sortCode.slice(0, 2)}-${sortCode.slice(2, 4)}-${sortCode.slice(4, 6)}`;
};

const getPaymentMethodDisplay = (paymentMethod) => {
  if (!paymentMethod) {
    return {
      icon: CreditCardIcon,
      title: "Unknown payment method",
      subtitle: null,
    };
  }

  if (paymentMethod.type === "bacs_debit") {
    return {
      icon: BuildingLibraryIcon,
      title: `UK bank account •••• ${paymentMethod.bacs_debit?.last4 || "----"}`,
      subtitle: paymentMethod.bacs_debit?.sort_code
        ? `Bacs Direct Debit · Sort code ${formatSortCode(paymentMethod.bacs_debit.sort_code)}`
        : "Bacs Direct Debit",
    };
  }

  if (paymentMethod.type === "card") {
    return {
      icon: CreditCardIcon,
      title: `${paymentMethod.card?.brand?.toUpperCase() || "CARD"} •••• ${paymentMethod.card?.last4 || "----"}`,
      subtitle: `Expires ${paymentMethod.card?.exp_month}/${paymentMethod.card?.exp_year}`,
    };
  }

  return {
    icon: CreditCardIcon,
    title: paymentMethod.type.replace(/_/g, " "),
    subtitle: null,
  };
};

const getSubscriptionStatusMeta = (subscription, billingIssue = null) => {
  if (billingIssue) {
    return {
      label: billingIssue.action_required ? "Action required" : "Payment retrying",
      color: billingIssue.action_required ? "red" : "amber",
      description: billingIssue.action_required
        ? "Payment failed and needs your attention. Update the payment method or pay the outstanding invoice."
        : "Payment failed. Stripe will retry it automatically; you can also update the payment method and retry now.",
    };
  }

  if (subscription?.payment_status === "processing") {
    return {
      label: "Processing",
      color: "amber",
      description: "The debit has been submitted to Stripe. Access will be enabled when the payment settles.",
    };
  }

  const statusMap = {
    active: {
      label: "Active",
      color: "green",
      description: "Your subscription is active and billing normally.",
    },
    trialing: {
      label: "Trial",
      color: "blue",
      description: "Your subscription is active and currently in a trial period.",
    },
    incomplete: {
      label: "Awaiting payment",
      color: "amber",
      description: "Finish the payment step to activate access.",
    },
    past_due: {
      label: "Past due",
      color: "amber",
      description: "A recent payment is overdue. Update your billing method if needed.",
    },
    unpaid: {
      label: "Unpaid",
      color: "red",
      description: "Your latest invoice was not paid.",
    },
    canceled: {
      label: "Canceled",
      color: "gray",
      description: "This subscription has been canceled.",
    },
  };

  return statusMap[subscription?.status] || {
    label: subscription?.status || "Unknown",
    color: "gray",
    description: "Subscription state unavailable.",
  };
};

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const buildBillingInfoFormState = (customer) => {
  const address = customer?.address || {};

  return {
    name: customer?.name || "",
    email: customer?.email || "",
    address: address.line1 || "",
    city: address.city || "",
    country: address.country || "",
    postalCode: address.postal_code || "",
  };
};

// ─── Shared sub-components ───────────────────────────────────────────────────

function PaymentMethodBadge({ pm, label }) {
  if (!pm) return null;

  const display = getPaymentMethodDisplay(pm);
  const Icon = display.icon;

  return (
    <Flex align="center" gap="2" p="2" style={{ background: "var(--gray-a2)", borderRadius: "var(--radius-2)" }}>
      <Icon width={16} height={16} style={{ flexShrink: 0 }} />
      <Flex direction="column" gap="0">
        {label && <Text size="1" color="gray">{label}</Text>}
        <Text size="2" weight="medium">{display.title}</Text>
        {display.subtitle && <Text size="1" color="gray">{display.subtitle}</Text>}
      </Flex>
    </Flex>
  );
}

function SubscriptionStatusSection({ subscription, billingIssue }) {
  const statusMeta = getSubscriptionStatusMeta(subscription, billingIssue);

  return (
    <Card style={{ display: "flex", flex: "0 0 auto", alignItems: "flex-start", alignSelf: "stretch", gap: "var(--space-4)", padding: "var(--space-4)" }}>
      <Flex gap="2" direction="column" style={{ width: "25%", alignItems: "flex-start" }}>
        <Flex gap="2" align="center" style={{ alignSelf: "stretch" }}>
          <ClipboardDocumentCheckIcon width={14} height={14} />
          <Text size="3" weight="medium">Subscription status</Text>
        </Flex>
        <Text size="1" color="gray">Your current subscription status and billing period.</Text>
      </Flex>
      <Flex gap="4" direction="column" style={{ flex: "1 0 0" }}>
        <Flex direction="column" gap="2">
          <Flex justify="end" align="center" style={{ alignSelf: "stretch" }}>
            <Badge color={statusMeta.color} size="2">{statusMeta.label}</Badge>
          </Flex>
          <Flex direction="column" gap="1">
            <Flex justify="between">
              <Text size="2" weight="medium">Current period</Text>
              <Text size="2" weight="medium" color="gray">
                {new Date(subscription.current_period_start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} to {new Date(subscription.current_period_end).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
              </Text>
            </Flex>
            <Text size="1" color="gray">{statusMeta.description}</Text>
            {subscription.cancel_at_period_end && (
              <Flex justify="between">
                <Text size="2" color="red">Cancels at period end</Text>
                <Badge color="red" size="1">Ending</Badge>
              </Flex>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Card>
  );
}

function ProductsCard({ items, title, description, invoiceSummary, nextInvoiceDate, paymentMethod, billingProducts, showPaymentBadge }) {
  const oneTimeProducts = billingProducts?.filter(p => p.product.billing_interval === "one_time") || [];
  const invoiceTotalDisplay = invoiceSummary
    ? (invoiceSummary.summary_type === "latest" ? "--" : formatCurrency(invoiceSummary.total, invoiceSummary.currency))
    : null;
  const invoiceDateDisplay = invoiceSummary
    ? (invoiceSummary.summary_type === "upcoming" ? nextInvoiceDate : invoiceSummary.period_end)
    : null;

  return (
    <Card style={{ display: "flex", flex: "0 0 auto", alignItems: "flex-start", alignSelf: "stretch", gap: "var(--space-4)", padding: "var(--space-4)" }}>
      <Flex gap="2" direction="column" style={{ width: "25%", alignItems: "flex-start" }}>
        <Flex gap="2" align="center" style={{ alignSelf: "stretch" }}>
          <BackpackIcon width={14} height={14} />
          <Text size="3" weight="medium">{title || "Products"}</Text>
        </Flex>
        <Text size="1" color="gray">{description || "Your active subscription products and pricing."}</Text>
      </Flex>
      <Flex gap="4" direction="column" style={{ flex: "1 0 0" }}>
        <Flex direction="column" gap="3" style={{ alignSelf: "stretch" }}>
          {/* Payment method badge at top if provided */}
          {showPaymentBadge && paymentMethod && (
            <PaymentMethodBadge pm={paymentMethod} label="Billed to" />
          )}
          {/* Recurring line items */}
          {items.map((item) => (
            <Flex key={item.id} justify="between" align="center">
              <Flex gap="1" align="center">
                <DotFilledIcon width={14} height={14} />
                <Text size="2">{item.product_name}{item.quantity > 1 && ` (x${item.quantity})`}</Text>
              </Flex>
              <Flex direction="column" align="end" gap="1">
                <Text size="3" weight="bold">
                  {getSubscriptionItemPriceLabel(item)}
                </Text>
                {getSubscriptionItemPriceNote(item) && (
                  <Text size="1" color="gray">{getSubscriptionItemPriceNote(item)}</Text>
                )}
              </Flex>
            </Flex>
          ))}
          {/* One-time products */}
          {oneTimeProducts.map((orgProduct) => (
            <Flex key={orgProduct.id} justify="between" align="center">
              <Flex gap="1" align="center">
                <DotFilledIcon width={14} height={14} />
                <Text size="2">{orgProduct.product.product_name}{orgProduct.quantity > 1 && ` (x${orgProduct.quantity})`}</Text>
              </Flex>
              <Text size="3" weight="bold">
                {getOrganisationProductPriceLabel(orgProduct)}
              </Text>
            </Flex>
          ))}
          {/* Subtotal / tax / total */}
          <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />
          <Flex justify="between" align="center">
            <Text size="2" color="gray">Recurring subtotal</Text>
            <Text size="2" color="gray" weight="medium">
              {formatCurrency(getFlatRecurringSubtotal(items), items[0]?.currency || "gbp")}
            </Text>
          </Flex>
          {invoiceSummary && invoiceSummary.summary_type === "upcoming" && hasBillableAmount(invoiceSummary.usage_amount) && (
            <Flex direction="column" gap="1">
              <Flex justify="between" align="center">
                <Text size="2" color="gray">Usage charges</Text>
                <Text size="2" color="gray" weight="medium">{formatCurrency(invoiceSummary.usage_amount, invoiceSummary.currency)}</Text>
              </Flex>
              <Text size="1" color="gray" style={{ fontStyle: "italic" }}>Estimated metered usage for the current billing period.</Text>
            </Flex>
          )}
          {invoiceSummary && invoiceSummary.summary_type === "upcoming" && hasBillableAmount(invoiceSummary.prorated_amount) && (
            <Flex direction="column" gap="1">
              <Flex justify="between" align="center">
                <Text size="2" color="gray">Prorated charges</Text>
                <Text size="2" color="gray" weight="medium">{formatCurrency(invoiceSummary.prorated_amount, invoiceSummary.currency)}</Text>
              </Flex>
              <Text size="1" color="gray" style={{ fontStyle: "italic" }}>Adjusted amount for subscription changes made during the current billing period.</Text>
            </Flex>
          )}
          {invoiceSummary && invoiceSummary.summary_type === "upcoming" && invoiceSummary.adjustments?.map((adjustment, index) => {
            const periodStart = adjustment.period_start
              ? new Date(adjustment.period_start).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
              : null;
            const periodEnd = adjustment.period_end
              ? new Date(adjustment.period_end).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
              : null;
            const periodLabel = periodStart && periodEnd ? `${periodStart} to ${periodEnd}` : null;

            return (
              <Flex key={`${adjustment.description}-${index}`} direction="column" gap="1">
                <Flex justify="between" align="center">
                  <Text size="2" color="gray">
                    {adjustment.is_previous_period ? "Previous unpaid subscription period" : "Additional charge"}
                  </Text>
                  <Text size="2" color="gray" weight="medium">{formatCurrency(adjustment.amount, invoiceSummary.currency)}</Text>
                </Flex>
                <Text size="1" color="gray" style={{ fontStyle: "italic" }}>
                  {[adjustment.description, periodLabel].filter(Boolean).join(" · ")}
                </Text>
              </Flex>
            );
          })}
          {invoiceSummary && invoiceSummary.tax > 0 && (
            <Flex justify="between" align="center">
              <Text size="2" color="gray">VAT (20%)</Text>
              <Text size="2" color="gray" weight="medium">{formatCurrency(invoiceSummary.tax, invoiceSummary.currency)}</Text>
            </Flex>
          )}
          {invoiceSummary && (
            <>
              <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />
              <Flex justify="between" align="center">
                <Text size="4" weight="bold">
                  {invoiceSummary.label}
                  {invoiceDateDisplay && (
                    <> ({new Date(invoiceDateDisplay).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })})</>
                  )}
                </Text>
                <Text size="4" weight="bold">{invoiceTotalDisplay}</Text>
              </Flex>
            </>
          )}
        </Flex>
      </Flex>
    </Card>
  );
}

function BillingInfoCard({ customer, countriesList, title, description, accentColor, payingCustomerId }) {
  const updateBillingDetails = useUpdateBillingDetails();
  const { setToastMessage } = useToast();
  const initialFormState = useMemo(
    () => buildBillingInfoFormState(customer),
    [
      customer?.name,
      customer?.email,
      customer?.address?.line1,
      customer?.address?.city,
      customer?.address?.country,
      customer?.address?.postal_code,
    ],
  );
  const [formState, setFormState] = useState(initialFormState);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setFormState(initialFormState);
    setErrors({});
  }, [initialFormState]);

  const isDirty = Object.keys(initialFormState).some((key) => formState[key] !== initialFormState[key]);

  const updateField = (field, value) => {
    setFormState((current) => ({ ...current, [field]: value }));
    if (errors[field]) {
      setErrors((current) => ({ ...current, [field]: "" }));
    }
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!formState.name.trim()) {
      nextErrors.name = "Name is required";
    }

    if (!formState.email.trim()) {
      nextErrors.email = "Email is required";
    } else if (!isValidEmail(formState.email.trim())) {
      nextErrors.email = "Enter a valid email address";
    }

    if (!formState.country) {
      nextErrors.country = "Country is required";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    updateBillingDetails.mutate(
      {
        ...(payingCustomerId && { paying_customer_id: payingCustomerId }),
        name: formState.name.trim(),
        email: formState.email.trim(),
        country: formState.country,
        address: formState.address.trim(),
        city: formState.city.trim(),
        postal_code: formState.postalCode.trim(),
      },
      {
        onError: () => {
          setToastMessage("Unable to update billing details. Please double check the information provided and try again.");
        },
      },
    );
  };

  return (
    <Card style={{ display: "flex", flex: "0 0 auto", alignItems: "flex-start", alignSelf: "stretch", gap: "var(--space-4)", padding: "var(--space-4)", borderLeft: accentColor ? `3px solid var(--${accentColor}-9)` : undefined }}>
      <Flex gap="2" direction="column" style={{ width: "25%", alignItems: "flex-start" }}>
        <Flex gap="2" align="center" style={{ alignSelf: "stretch" }}>
          <UserIcon width={14} height={14} />
          <Text size="3" weight="medium">{title || "Billing information"}</Text>
        </Flex>
        <Text size="1" color="gray">{description || "Update your billing contact details and address."}</Text>
      </Flex>
      <Flex gap="4" direction="column" style={{ alignItems: "flex-end", flex: "1 0 0" }}>
        <Text as="label" size="2" style={{ alignSelf: "stretch" }}>
          <Flex gap="1" direction="column">
            <Text size="2" weight="medium">Name</Text>
            <TextField.Root
              size="2"
              value={formState.name}
              onChange={(event) => updateField("name", event.target.value)}
            />
            {errors.name && <Text size="1" color="red">{errors.name}</Text>}
          </Flex>
        </Text>
        <Text as="label" size="2" style={{ alignSelf: "stretch" }}>
          <Flex gap="1" direction="column">
            <Text size="2" weight="medium">Email</Text>
            <TextField.Root
              size="2"
              type="email"
              value={formState.email}
              onChange={(event) => updateField("email", event.target.value)}
            />
            {errors.email && <Text size="1" color="red">{errors.email}</Text>}
          </Flex>
        </Text>
        <Text as="label" size="2" style={{ alignSelf: "stretch" }}>
          <Flex gap="1" direction="column">
            <Text size="2" weight="medium">Address</Text>
            <TextField.Root
              size="2"
              placeholder="123 Main Street"
              value={formState.address}
              onChange={(event) => updateField("address", event.target.value)}
            />
          </Flex>
        </Text>
        <Text as="label" size="2" style={{ alignSelf: "stretch" }}>
          <Flex gap="1" direction="column">
            <Text size="2" weight="medium">City</Text>
            <TextField.Root
              size="2"
              placeholder="London"
              value={formState.city}
              onChange={(event) => updateField("city", event.target.value)}
            />
          </Flex>
        </Text>
        <Flex gap="3" direction={{ initial: "column", sm: "row" }} style={{ alignSelf: "stretch", width: "100%" }}>
          <Text as="label" size="2" style={{ flex: 1 }}>
            <Flex gap="1" direction="column">
              <Text size="2" weight="medium">Country</Text>
              <Select.Root value={formState.country} onValueChange={(value) => updateField("country", value)}>
                <Select.Trigger placeholder="Select country" style={{ width: "100%" }} />
                <Select.Content style={{ maxHeight: "300px" }} position="popper">
                  {countriesList.map((country) => (
                    <Select.Item key={country.code} value={country.code}>
                      {country.name}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
              {errors.country && <Text size="1" color="red">{errors.country}</Text>}
            </Flex>
          </Text>
          <Text as="label" size="2" style={{ flex: 1 }}>
            <Flex gap="1" direction="column">
              <Text size="2" weight="medium">Postal Code</Text>
              <TextField.Root
                size="2"
                placeholder="SW1A 1AA"
                value={formState.postalCode}
                onChange={(event) => updateField("postalCode", event.target.value)}
              />
            </Flex>
          </Text>
        </Flex>
        <Button
          variant="solid"
          disabled={!isDirty}
          loading={updateBillingDetails.isPending}
          onClick={handleSave}
        >
          Save changes
        </Button>
      </Flex>
    </Card>
  );
}

function PaymentMethodCard({
  defaultPm,
  allMethods,
  onUpdatePaymentMethod,
  canUpdatePaymentMethod = true,
  cancellationReason = null,
}) {
  return (
    <Card style={{ display: "flex", flex: "0 0 auto", alignItems: "flex-start", alignSelf: "stretch", gap: "var(--space-4)", padding: "var(--space-4)" }}>
      <Flex gap="2" direction="column" style={{ width: "25%", alignItems: "flex-start" }}>
        <Flex gap="2" align="center" style={{ alignSelf: "stretch" }}>
          <CreditCardIcon width={14} height={14} />
          <Text size="3" weight="medium">Payment methods</Text>
        </Flex>
        <Text size="1" color="gray">Saved cards and UK bank accounts used for subscription billing.</Text>
      </Flex>
      <Flex gap="3" direction="column" style={{ flex: "1 0 0" }}>
        {!canUpdatePaymentMethod && cancellationReason && (
          <Text size="2" color="gray">{cancellationReason}</Text>
        )}
        {allMethods.length === 0 && (
          <Text size="2" color="gray">No saved payment methods on file.</Text>
        )}
        {allMethods.map((pm) => (
          <Flex key={pm.id} justify="between" align="center" p="2">
            <Flex align="center" gap="3">
              {(() => {
                const display = getPaymentMethodDisplay(pm);
                const Icon = display.icon;
                return <Icon width={16} height={16} style={{ flexShrink: 0 }} />;
              })()}
              <Flex direction="column" gap="0">
                <Flex align="center" gap="2">
                  <Text size="2" weight="medium">{getPaymentMethodDisplay(pm).title}</Text>
                  {pm.is_default && <Badge size="1" color="blue">Default</Badge>}
                </Flex>
                {getPaymentMethodDisplay(pm).subtitle && (
                  <Text size="1" color="gray">{getPaymentMethodDisplay(pm).subtitle}</Text>
                )}
              </Flex>
            </Flex>
            {canUpdatePaymentMethod && (
              <Button size="2" variant="soft" color="gray" onClick={onUpdatePaymentMethod}>
                <Pencil1Icon />
                Update card
              </Button>
            )}
          </Flex>
        ))}
      </Flex>
    </Card>
  );
}

function BillingPaymentIssueCard({ issue, payingCustomerId, onUpdatePaymentMethod }) {
  const retryInvoicePayment = useRetryInvoicePayment();
  const { setToastMessage } = useToast();

  if (!issue) {
    return null;
  }

  const handleRetry = async () => {
    try {
      const response = await retryInvoicePayment.mutateAsync({
        invoice_id: issue.invoice_id,
        ...(payingCustomerId && { paying_customer_id: payingCustomerId }),
      });
      const result = response?.data || response;
      if (result.paid) {
        setToastMessage("Outstanding invoice paid successfully.");
        return;
      }
      if (result.requires_action && result.hosted_invoice_url) {
        window.open(result.hosted_invoice_url, "_blank", "noopener,noreferrer");
        setToastMessage("Complete the payment securely in the Stripe invoice page.");
        return;
      }
      setToastMessage("Payment is still pending. Stripe will continue the recovery schedule.");
    } catch (error) {
      setToastMessage(error.response?.data?.error || "Payment retry failed. Please update the payment method and try again.");
    }
  };

  const nextRetry = issue.next_payment_attempt
    ? new Date(issue.next_payment_attempt).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })
    : null;

  return (
    <Card style={{ alignSelf: "stretch", padding: "var(--space-4)", background: "var(--red-a2)", border: "1px solid var(--red-a6)" }}>
      <Flex direction="column" gap="3">
        <Flex justify="between" align="start" gap="4">
          <Flex direction="column" gap="1">
            <Text size="3" weight="bold" color="red">Payment outstanding</Text>
            <Text size="2" weight="medium" color="red">
              {formatCurrency(issue.amount_due, issue.currency)} remains due on invoice {issue.invoice_id}.
            </Text>
            {issue.failure_message && <Text size="1" color="gray">{issue.failure_message}</Text>}
            <Text size="1" color="gray">
              Attempt {issue.attempt_count || 1}{nextRetry ? ` · Next automatic retry ${nextRetry}` : " · Update the payment method before retrying"}
            </Text>
          </Flex>
          <Badge color={issue.action_required ? "red" : "amber"}>
            {issue.action_required ? "Action required" : "Retrying"}
          </Badge>
        </Flex>
        <Flex justify="end" gap="2" wrap="wrap">
          {issue.hosted_invoice_url && (
            <Button variant="soft" color="gray" onClick={() => window.open(issue.hosted_invoice_url, "_blank", "noopener,noreferrer")}>
              View invoice
            </Button>
          )}
          <Button variant="soft" onClick={onUpdatePaymentMethod}>Update payment method</Button>
          <Button onClick={handleRetry} loading={retryInvoicePayment.isPending}>
            Retry payment now
          </Button>
        </Flex>
      </Flex>
    </Card>
  );
}

function InvoicesCard({ invoices }) {
  return (
    <Card style={{ display: "flex", flex: "0 0 auto", alignItems: "flex-start", alignSelf: "stretch", gap: "var(--space-4)", padding: "var(--space-4)" }}>
      <Flex gap="2" direction="column" style={{ width: "25%", alignItems: "flex-start" }}>
        <Flex gap="2" align="center" style={{ alignSelf: "stretch" }}>
          <NewspaperIcon width={14} height={14} />
          <Text size="3" weight="medium">Invoice history</Text>
        </Flex>
        <Text size="1" color="gray">View and download your past invoices.</Text>
      </Flex>
      <Flex gap="4" direction="column" style={{ flex: "1 0 0" }}>
        {invoices.length === 0 ? (
          <Text size="2" color="gray">No invoices yet</Text>
        ) : (
          <Flex direction="column" style={{ alignSelf: "stretch" }}>
            {invoices.map((inv, index) => (
              <Flex key={inv.id} direction="column">
                <Flex justify="between" align="center" p="1">
                  <Flex direction="column" gap="1">
                    <Text size="2" weight="medium">
                      {inv.number || inv.id}
                      <Badge color={inv.status === "paid" ? "green" : inv.status === "open" ? "amber" : "gray"} size="1" style={{ marginLeft: "var(--space-2)" }}>
                        {inv.status === "paid" ? "Paid" : inv.status}
                      </Badge>
                    </Text>
                    <Text size="1" color="gray">{new Date(inv.created).toLocaleDateString()}</Text>
                  </Flex>
                  <Flex align="center" gap="3">
                    <Text size="2" weight="bold">{formatCurrency(getInvoiceDisplayAmount(inv), inv.currency)}</Text>
                    {inv.hosted_invoice_url && (
                      <Button size="1" variant="soft" color="gray" onClick={() => window.open(inv.hosted_invoice_url, "_blank")}>View</Button>
                    )}
                  </Flex>
                </Flex>
                {index < invoices.length - 1 && <Separator size="4" my="2" />}
              </Flex>
            ))}
          </Flex>
        )}
      </Flex>
    </Card>
  );
}

// ─── Pre-activation views (no subscription yet) ──────────────────────────────

function PreActivationSingleSub({ products, onActivate, label }) {
  return (
    <Card style={{ display: "flex", flex: "0 0 auto", alignItems: "flex-start", alignSelf: "stretch", gap: "var(--space-4)", padding: "var(--space-4)" }}>
      <Flex gap="2" direction="column" style={{ width: "25%", alignItems: "flex-start" }}>
        <Flex gap="2" align="center" style={{ alignSelf: "stretch" }}>
          <BackpackIcon width={14} height={14} />
          <Text size="3" weight="medium">{label || "Products"}</Text>
        </Flex>
        <Text size="1" color="gray">All products will be billed to a single payment method.</Text>
      </Flex>
      <Flex gap="4" direction="column" style={{ alignItems: "flex-end", flex: "1 0 0" }}>
        <Flex direction="column" gap="4" style={{ alignSelf: "stretch" }}>
          <Flex direction="column" gap="2" style={{ alignSelf: "stretch" }}>
            {products.map((op) => (
              <Flex key={op.id} justify="between" align="center" style={{ alignSelf: "stretch" }}>
                <Flex gap="1" align="center">
                  <DotFilledIcon width={14} height={14} />
                  <Text size="2">{op.product.product_name}{op.quantity > 1 && ` (x${op.quantity})`}</Text>
                </Flex>
                <Text size="2" weight="bold">
                  {getOrganisationProductPriceLabel(op)}
                </Text>
              </Flex>
            ))}
          </Flex>
          <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />
          <Flex justify="between" align="center" style={{ alignSelf: "stretch" }}>
            <Text size="4" weight="bold" style={{ marginLeft: "var(--space-4)" }}>Total amount</Text>
            <Text size="4" weight="bold">
              {formatCurrency(getFlatOrganisationProductsTotal(products), products[0]?.product.currency || "gbp")}/month
            </Text>
          </Flex>
        </Flex>
        <Button size="2" variant="solid" onClick={onActivate}>Activate subscription</Button>
      </Flex>
    </Card>
  );
}

// (Multi-sub pre-activation removed — future feature)

// ─── Post-activation: single subscription view ──────────────────────────────

function ActiveSingleSubView({
  details,
  products,
  invoices,
  countriesList,
  payingCustomerId,
  onUpdatePaymentMethod,
  canUpdatePaymentMethod,
  paymentMethodNote,
}) {
  return (
    <>
      <SubscriptionStatusSection subscription={details.subscription} billingIssue={details.billing_payment_issue} />
      <BillingPaymentIssueCard
        issue={details.billing_payment_issue}
        payingCustomerId={payingCustomerId}
        onUpdatePaymentMethod={onUpdatePaymentMethod}
      />
      <ProductsCard
        items={details.subscription.items}
        invoiceSummary={details.subscription.upcoming_invoice || details.subscription.invoice_summary}
        nextInvoiceDate={details.subscription.current_period_end}
        billingProducts={products}
        showPaymentBadge={false}
      />
      <BillingInfoCard customer={details.customer} countriesList={countriesList} payingCustomerId={payingCustomerId} />
      <PaymentMethodCard
        defaultPm={details.customer.default_payment_method}
        allMethods={details.payment_methods}
        onUpdatePaymentMethod={onUpdatePaymentMethod}
        canUpdatePaymentMethod={canUpdatePaymentMethod}
        cancellationReason={paymentMethodNote}
      />
      <InvoicesCard invoices={invoices} />
    </>
  );
}

// ─── Per-payer panel (uses its own hooks) ────────────────────────────────────

function PayerPanel({ payerId, payerLabel, payerProducts, countriesList, details, invoices }) {
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [paymentMethodModalOpen, setPaymentMethodModalOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const cancelSubscription = useCancelSubscription();

  const hasSubscription = !!details.subscription;
  const isSubscriptionCanceled = details.subscription?.status === "canceled";
  console.log("SUB CURRENCYS:", details.subscription?.items);
  const subscriptionCurrencies = [...new Set(
    (details.subscription?.items || [])
      .map((item) => item.currency?.toLowerCase())
      .filter(Boolean)
  )];
  const allowBacsUpdate = subscriptionCurrencies.length > 0
    ? subscriptionCurrencies.length === 1 && subscriptionCurrencies[0] === "gbp"
    : details.customer?.currency?.toLowerCase() === "gbp";

  const handleCancelSubscription = () => {
    cancelSubscription.mutate(payerId, {
      onSuccess: () => setCancelDialogOpen(false),
      onError: () => setCancelDialogOpen(false),
    });
  };

  if (!hasSubscription) {
    return (
      <>
        <PreActivationSingleSub 
          products={payerProducts} 
          onActivate={() => setActivateModalOpen(true)} 
          label={payerLabel} 
        />
        <ActivateSubscriptionModal
          open={activateModalOpen}
          onOpenChange={setActivateModalOpen}
          products={payerProducts}
          payingCustomerId={payerId}
        />
      </>
    );
  }

  return (
    <>
      <Flex gap="2" direction="column" style={{ alignSelf: "stretch" }}>
        {payerLabel && (
          <Text size="2" weight="bold" color="gray" style={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>
            {payerLabel}
          </Text>
        )}
        <Flex gap="5" direction="column" style={{ alignSelf: "stretch" }}>
          <ActiveSingleSubView
            details={details}
            products={payerProducts}
            invoices={invoices}
            countriesList={countriesList}
            payingCustomerId={payerId}
            onUpdatePaymentMethod={() => setPaymentMethodModalOpen(true)}
            canUpdatePaymentMethod={!isSubscriptionCanceled}
            paymentMethodNote={isSubscriptionCanceled ? "Subscription canceled in Stripe. DO NOT attempt to manually pay any open invoices." : null}
          />
        </Flex>
      </Flex>

      {/* Cancel subscription */}
      {details.subscription && !details.subscription.cancel_at_period_end && !isSubscriptionCanceled && (
        <Flex justify="end" style={{ alignSelf: "stretch" }}>
          <AlertDialog.Root open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
            <AlertDialog.Trigger>
              <Button size="2" variant="solid" color="red">Cancel subscription</Button>
            </AlertDialog.Trigger>
            <AlertDialog.Content maxWidth="450px">
              <AlertDialog.Title>Cancel subscription</AlertDialog.Title>
              <AlertDialog.Description size="2">
                Your subscription will remain active until the end of the current billing period
                ({new Date(details.subscription.current_period_end).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}).
                After that, access to all products will be disabled.
              </AlertDialog.Description>
              <Flex gap="3" mt="4" justify="end">
                <AlertDialog.Cancel>
                  <Button variant="soft" color="gray">Keep subscription</Button>
                </AlertDialog.Cancel>
                <AlertDialog.Action>
                  <Button
                    variant="solid"
                    color="red"
                    onClick={handleCancelSubscription}
                    disabled={cancelSubscription.isPending}
                  >
                    {cancelSubscription.isPending ? "Cancelling…" : "Yes, cancel"}
                  </Button>
                </AlertDialog.Action>
              </Flex>
            </AlertDialog.Content>
          </AlertDialog.Root>
        </Flex>
      )}

      {details.subscription?.cancel_at_period_end && (
        <Card style={{ alignSelf: "stretch", padding: "var(--space-3)", background: "var(--amber-a2)", border: "1px solid var(--amber-a5)" }}>
          <Flex align="center" gap="2">
            <Text size="2" color="amber" weight="bold">
              Subscription will end on {new Date(details.subscription.current_period_end).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
            </Text>
          </Flex>
        </Card>
      )}

      {/* Update payment method modal */}
      <AddPaymentMethodModal
        open={paymentMethodModalOpen}
        onOpenChange={setPaymentMethodModalOpen}
        payingCustomerId={payerId}
        allowBacs={allowBacsUpdate}
        customer={details.customer}
        countriesList={countriesList}
      />
    </>
  );
}

// ─── Main page component ─────────────────────────────────────────────────────

export default function OrganisationBilling() {
  const { setToastMessage } = useToast();
  const { mutateAsync: confirmPaymentMethod } = useConfirmPaymentMethod();
  const dismissBillingFailureNotice = useDismissBillingFailureNotice();
  const [activeBillingFailureNotice, setActiveBillingFailureNotice] = useState(null);
  const [suppressedBillingFailureNoticePayerId, setSuppressedBillingFailureNoticePayerId] = useState(null);

  const countriesList = useMemo(() => {
    const countryObj = countries.getNames("en", { select: "official" });
    return Object.entries(countryObj).map(([code, name]) => ({ code, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const checkoutSessionId = searchParams.get("billing_setup_session_id");
    const payerId = searchParams.get("billing_setup_payer_id");
    const cancelled = searchParams.get("billing_setup_cancelled");
    console.log("Billing setup params:", { checkoutSessionId, payerId, cancelled });

    if (!checkoutSessionId && !cancelled) {
      return;
    }

    const clearBillingSetupParams = () => {
      const nextParams = new URLSearchParams(window.location.search);
      ["billing_setup_session_id", "billing_setup_payer_id", "billing_setup_cancelled"].forEach((key) => {
        nextParams.delete(key);
      });

      const nextQuery = nextParams.toString();
      const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ""}${window.location.hash}`;
      window.history.replaceState({}, "", nextUrl);
    };

    if (cancelled) {
      setToastMessage("Bacs update was cancelled.");
      clearBillingSetupParams();
      return;
    }

    let disposed = false;

    const finalizeCheckoutSetup = async () => {
      try {
        await confirmPaymentMethod({
          checkout_session_id: checkoutSessionId,
          ...(payerId && { paying_customer_id: payerId }),
        });

        if (!disposed) {
          setToastMessage("Payment method updated successfully!");
        }
      } catch (error) {
        if (!disposed) {
          setToastMessage(error.response?.data?.error || "Failed to update payment method");
        }
      } finally {
        if (!disposed) {
          clearBillingSetupParams();
        }
      }
    };

    finalizeCheckoutSetup();

    return () => {
      disposed = true;
    };
  }, [confirmPaymentMethod, setToastMessage]);

  // ── Real API hooks ──
  const { data: productsRes, isLoading: productsLoading, error: productsError } = useBillingProducts();

  // ── Derived state ──
  const products = productsRes?.data || [];

  // ── Group products by paying customer ──
  const payers = useMemo(() => {
    const payerGroups = {};
    for (const p of products) {
      const pcId = p.paying_customer_id || "unassigned";
      if (!payerGroups[pcId]) {
        payerGroups[pcId] = { id: pcId, label: p.paying_customer_label || "Primary", products: [] };
      }
      payerGroups[pcId].products.push(p);
    }
    return Object.values(payerGroups);
  }, [products]);

  // ── Fetch billing details & invoices for all payers in parallel ──
  const payerIds = useMemo(() => payers.map(p => p.id === "unassigned" ? null : p.id), [payers]);

  const detailsQueries = useQueries({
    queries: payerIds.map(id => ({
      queryKey: ["billing-details", id],
      queryFn: () => getBillingDetails(id),
      staleTime: 1000 * 60 * 5,
      retry: 1,
      enabled: products.length > 0,
    })),
  });

  const invoicesQueries = useQueries({
    queries: payerIds.map(id => ({
      queryKey: ["billing-invoices", 10, null, id],
      queryFn: () => getInvoices(10, null, id),
      staleTime: 1000 * 60 * 5,
      enabled: products.length > 0,
    })),
  });

  const anyLoading = productsLoading
    || detailsQueries.some(q => q.isLoading)
    || invoicesQueries.some(q => q.isLoading);

  useEffect(() => {
    if (anyLoading || activeBillingFailureNotice) {
      return;
    }

    let suppressedNoticeStillPending = false;

    for (let index = 0; index < payers.length; index += 1) {
      const details = detailsQueries[index]?.data?.data;
      const notice = details?.billing_failure_notice;
      const payerId = payers[index].id === "unassigned" ? null : payers[index].id;

      if (notice?.pending && payerId === suppressedBillingFailureNoticePayerId) {
        suppressedNoticeStillPending = true;
        continue;
      }

      if (notice?.pending) {
        setActiveBillingFailureNotice({
          payerId,
          payerLabel: payers[index].label,
          message: notice.message,
        });
        return;
      }
    }

    if (suppressedBillingFailureNoticePayerId && !suppressedNoticeStillPending) {
      setSuppressedBillingFailureNoticePayerId(null);
    }
  }, [anyLoading, activeBillingFailureNotice, payers, detailsQueries, suppressedBillingFailureNoticePayerId]);

  const handleDismissBillingFailureNotice = async () => {
    if (!activeBillingFailureNotice) {
      return;
    }

    try {
      const dismissedPayerId = activeBillingFailureNotice.payerId;
      await dismissBillingFailureNotice.mutateAsync({
        ...(activeBillingFailureNotice.payerId && { paying_customer_id: activeBillingFailureNotice.payerId }),
      });
      setSuppressedBillingFailureNoticePayerId(dismissedPayerId);
      setActiveBillingFailureNotice(null);
    } catch (error) {
      setToastMessage(error.response?.data?.error || "Failed to dismiss billing notice");
    }
  };

  // ── Loading state ──
  if (anyLoading) {
    return (
      <div style={{ position: "fixed", top: 0, left: 0, width: "100%", zIndex: 1000 }}>
        <Progress size="1" duration="1" />
      </div>
    );
  }

  // ── Error state ──
  if (productsError) {
    return (
      <Flex direction="column" align="center" justify="center" gap="3" style={{ width: "100%", height: "100%", flex: "1 0 0" }}>
        <Text size="3" weight="bold" color="red">Failed to load billing information</Text>
        <Text size="2" color="gray">{productsError?.message || "Please try again later."}</Text>
      </Flex>
    );
  }

  // ── No products assigned (Trial period) ──
  if (products.length === 0) {
    return (
      <Flex p="5" direction="column" gap="5" style={{ alignSelf: "stretch", alignItems: "center",
                    justifyContent: "center", width: "100%", borderRadius: "8px", border: "1px dashed var(--gray-a6)",
                    flex: 1, position: "relative", backgroundColor: "var(--color-background)" }}>
        <Flex direction="column" align="center" gap="2" maxWidth="400px" style={{alignItems: 'center', width: '100%'}}>
          <Text size="4" weight="medium" align="center">You are currently on trial period.</Text>
          <Text size="2" weight="regular" align="center" color="gray">
            Administrator will set up billing products for your organisation after trial period is over.
          </Text>
        </Flex>
      </Flex>
    );
  }

  // ── Group products by paying customer ── (already computed above)

  return (
    <>
      <Flex direction="column" style={{ alignSelf: "stretch", width: "100%", height: "100%", alignItems: "center", flex: "1 0 0" }}>
        <Flex direction="column" gap="5" style={{ alignSelf: "stretch", flex: "1 0 0", alignItems: "center", overflowY: "scroll" }}>
          {payers.map((payer, index) => {
            const details = detailsQueries[index]?.data?.data || {
              customer: null,
              subscription: null,
              payment_methods: [],
              billing_failure_notice: null,
              billing_payment_issue: null,
            };
            const invoices = invoicesQueries[index]?.data?.data?.invoices || [];
            return (
              <PayerPanel
                key={payer.id}
                payerId={payer.id === "unassigned" ? null : payer.id}
                payerLabel={payers.length > 1 ? payer.label : null}
                payerProducts={payer.products}
                countriesList={countriesList}
                details={details}
                invoices={invoices}
              />
            );
          })}
        </Flex>
        <Dialog.Root open={Boolean(activeBillingFailureNotice)}>
          <Dialog.Content style={{ maxWidth: 520 }}>
            <Dialog.Title>Payment failed</Dialog.Title>
            <Dialog.Description size="2" color="gray" mb="4">
              {activeBillingFailureNotice?.message || "A recent payment failed and this subscription needs to be activated again."}
            </Dialog.Description>
            {activeBillingFailureNotice?.payerLabel && (
              <Text size="2" color="gray">
                Payer: {activeBillingFailureNotice.payerLabel}
              </Text>
            )}
            <Flex justify="end" mt="4">
              <Button onClick={handleDismissBillingFailureNotice} loading={dismissBillingFailureNotice.isPending}>
                Close
              </Button>
            </Flex>
          </Dialog.Content>
        </Dialog.Root>
      </Flex>
      <ToastAlert />
    </>
  );
}
